import { useCallback, useEffect, useRef, useState } from 'react';

import { useVoiceAssistantStore } from '../../store/useVoiceAssistantStore';
import { SAMPLE_DOCUMENTS, type SampleDocument } from './sampleDocuments';

type SampleKind = keyof typeof SAMPLE_DOCUMENTS;

/**
 * Streams a sample document into the **already-open** workspace: sets the
 * component, walks the progress steps, and streams the body word-by-word to
 * ready. Stands in for the live agent drafting until the ml-scribe
 * `open_workspace` tool is wired.
 *
 * Must be hosted by a component that stays mounted across the shell → draft
 * transition (i.e. the overlay itself), not the empty state it replaces.
 */
export function useWorkspaceStreamer() {
  const [streaming, setStreaming] = useState(false);
  const cancelled = useRef(false);
  const timers = useRef<Array<ReturnType<typeof setTimeout>>>([]);

  const clearTimers = useCallback(() => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
  }, []);

  const delay = useCallback(
    (ms: number) =>
      new Promise<void>((resolve) => {
        timers.current.push(setTimeout(resolve, ms));
      }),
    []
  );

  const stream = useCallback(
    async (kind: SampleKind) => {
      const store = useVoiceAssistantStore.getState();
      if (!store.workspace) return;
      const sample: SampleDocument = SAMPLE_DOCUMENTS[kind];
      cancelled.current = false;
      clearTimers();
      setStreaming(true);

      store.setWorkspaceComponent({
        kind: 'draft-document',
        documentKind: sample.documentKind,
        title: sample.title,
        subtitle: sample.subtitle,
        content: '',
        status: 'streaming',
        steps: sample.steps.map((step) => ({ ...step, status: 'pending' })),
      });

      const guard = () =>
        cancelled.current || !useVoiceAssistantStore.getState().workspace;
      const words = sample.body.split(/(\s+)/);

      for (const step of sample.steps) {
        store.updateWorkspaceStep(step.id, 'active');
        await delay(600);
        if (guard()) return;
        // The body streams in during the "draft" step; other steps are quick.
        if (step.id === 'draft') {
          for (const word of words) {
            await delay(26);
            if (guard()) return;
            store.appendWorkspaceContent(word);
          }
        }
        store.updateWorkspaceStep(step.id, 'done');
      }

      store.setWorkspaceStatus('ready');
      setStreaming(false);
    },
    [clearTimers, delay]
  );

  useEffect(
    () => () => {
      cancelled.current = true;
      clearTimers();
    },
    [clearTimers]
  );

  return { stream, streaming };
}
